import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { ArrowLeftRightIcon, FileImageIcon, Trash2Icon } from "lucide-react";

import { useThrottle } from "simplydo/react/hooks";
import { css, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";

import api from "api";
import util from "utils/utils";

import SuggestedIdeaCard from "components/lib/Cards/SuggestedIdeaCard";
import { ImageChooser } from "components/lib/Choosers";
import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";

import type {
  IdeaChallenge,
  IdeaOmniSearchResult,
  UpdateIdeaFn,
} from "../types";

const bodyContainerClass = css`
  border: none;
`;

const CoverEditor = styled.div`
  border: 1px solid rgb(220, 220, 220);
  padding: 10px;
  border-radius: 4px;
  margin-top: 10px;
`;

const CoverPreview = styled.div<{ $imageUrl: string }>`
  width: 140px;
  height: 140px;
  margin: 10px auto;
  border-radius: 12px;
  background-size: cover;
  background-position: center center;
  background-image: ${({ $imageUrl }) =>
    $imageUrl ? `url(${$imageUrl})` : "none"};
  background-color: #d0d5dc;
`;

const CoverActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
  text-align: center;
  align-self: center;
`;

const IdeaNameField = ({
  challenge,
  isEditingName,
  isEditingCover,
  ideaCoverImage,
  ideaId,
  ideaName: propIdeaName,
  updateIdea,
}: {
  challenge?: IdeaChallenge;
  isEditingName?: boolean;
  isEditingCover?: boolean;
  ideaCoverImage?: string;
  ideaId: string;
  ideaName?: string;
  updateIdea: UpdateIdeaFn;
}) => {
  const [searchResults, setSearchResults] = useState<IdeaOmniSearchResult[]>(
    [],
  );
  const [previewId, setPreviewId] = useState("");
  const [nameFocused, setNameFocused] = useState(false);
  const { t } = useTranslation();
  const coverImageEnabled = !challenge?.preventIdeaCoverImages;

  const isDefaultCoverImage =
    ideaCoverImage === util.ideaCoverImage(undefined) ||
    !!ideaCoverImage?.includes("images/idea.png");

  const defaultIdeaName = `${t("common:capitalise", { key: "generic.idea" })} for ${challenge?.name}`;
  const isDefaultName = propIdeaName === defaultIdeaName;
  const ideaName = isDefaultName ? "" : (propIdeaName ?? "");
  const shouldSearch = !!isEditingName && ideaName.length > 2;
  const filteredResults = shouldSearch
    ? searchResults.filter((r) => r._id !== ideaId)
    : [];

  const updateName = useCallback(
    (value: string) => {
      if (value.length === 0) {
        value = defaultIdeaName;
      }
      updateIdea(value, ["name"]);
    },
    [updateIdea, defaultIdeaName],
  );

  const throttledSearch = useThrottle((ideaId, term) => {
    if (!term) return;
    api.search.omni(
      { query: term, include: ["ideas"] },
      ({ ideas }) => {
        setSearchResults(ideas.filter((r) => r._id !== ideaId));
      },
      () => {},
    );
  }, 1000);

  const searchIdeas = useCallback(() => {
    throttledSearch(ideaId, ideaName);
  }, [ideaId, ideaName, throttledSearch]);

  useEffect(() => {
    if (shouldSearch) {
      searchIdeas();
    }
  }, [shouldSearch, searchIdeas]);

  const setCoverImage = useCallback(
    (filename: string | null, _name: string | null, url: string | null) => {
      updateIdea(filename, ["coverImage"]);
      if (!filename) {
        updateIdea([], ["coverImageThumbnails"]);
        updateIdea({}, ["coverImageThumbnailUrls"]);
      }
      updateIdea(url, ["coverImageUrl"], true);
    },
    [updateIdea],
  );

  const showCoverEditor = isEditingCover && coverImageEnabled;
  if (!isEditingName && !showCoverEditor) return null;
  const sorted = [...filteredResults].sort(
    (a, b) => (b.completeness ?? 0) - (a.completeness ?? 0),
  );
  const coverPreviewUrl = util.mixinCssUrlFallback(
    ideaCoverImage ?? "",
    util.ideaCoverImage(undefined),
  );

  return (
    <Grid mobile={1}>
      <GridColumn
        className={bodyContainerClass}
        style={{ borderRadius: 5, marginBottom: 25 }}
      >
        <HandledIdeaPreview
          ideaId={previewId}
          onCloseModal={() => setPreviewId("")}
        />
        <Card fluid>
          <CardContent>
            {isEditingName ? (
              <>
                <div style={{ marginBottom: 5 }}>
                  <H3>
                    {t("common:capitalise", { key: "generic.idea" })} name
                    <Subheader>
                      {challenge?.ideaNameHelpText ||
                        t("ideas.name.auto.suggestion")}
                    </Subheader>
                  </H3>
                </div>
                <Input
                  size="large"
                  placeholder={t("ideas.submit.naming.placeholder")}
                  fluid
                  value={ideaName}
                  onChange={(e) => {
                    if (ideaName !== e.target.value) {
                      updateName(e.target.value);
                    }
                  }}
                  onFocus={() => setNameFocused(true)}
                />
                {nameFocused && filteredResults.length > 0 && (
                  <>
                    <Divider />
                    <H4>
                      Related {t("generic.ideas")}
                      <Subheader>
                        Here are some similar {t("generic.ideas")} we found
                        based on the name you entered...
                      </Subheader>
                    </H4>
                    <Grid
                      style={{ marginBottom: 10, marginTop: 5 }}
                      columns={3}
                      mobile={1}
                    >
                      {sorted.slice(0, 3).map((result) => (
                        <GridColumn key={result._id}>
                          <SuggestedIdeaCard
                            onHeaderClicked={() => setPreviewId(result._id)}
                            idea={result}
                          />
                        </GridColumn>
                      ))}
                    </Grid>
                  </>
                )}
              </>
            ) : null}
            {showCoverEditor ? (
              <>
                <H5>
                  {t("users.edit.coverImage.title")}
                  <Subheader>
                    {t("ideas.coverimage.info")} We recommend using an image of
                    size <b>500px</b> by <b>500px</b> for the cover image.
                  </Subheader>
                </H5>
                {isDefaultCoverImage ? (
                  <ImageChooser
                    forType="idea"
                    forId={ideaId}
                    onComplete={setCoverImage}
                    trigger={
                      <Button fluid color="primary">
                        <Icon icon={FileImageIcon} />
                        {t("users.edit.coverImage.select")}
                      </Button>
                    }
                  />
                ) : (
                  <CoverEditor>
                    <Grid columns={2}>
                      <GridColumn span={2} computer={1}>
                        <CoverPreview $imageUrl={coverPreviewUrl} />
                      </GridColumn>
                      <GridColumn span={2} computer={1}>
                        <CoverActions>
                          <H5>{t("ideas.coverimage.manage")}</H5>
                          <ImageChooser
                            forType="idea"
                            forId={ideaId}
                            onComplete={setCoverImage}
                            trigger={
                              <Button fluid color="secondary" variant="outline">
                                <Icon icon={ArrowLeftRightIcon} />
                                {t("generic.replace")}
                              </Button>
                            }
                          />
                          <Button
                            fluid
                            variant="outline"
                            onClick={() =>
                              util
                                .confirm(
                                  "Remove cover image",
                                  `Are you sure you want to remove the cover image for this ${t("generic.idea")}?`,
                                )
                                .then(() => setCoverImage(null, null, null))
                                .catch(() => {})
                            }
                          >
                            <Icon icon={Trash2Icon} />
                            {t("generic.remove")}
                          </Button>
                        </CoverActions>
                      </GridColumn>
                    </Grid>
                  </CoverEditor>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>
      </GridColumn>
    </Grid>
  );
};

export default IdeaNameField;
